// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useRef } from 'react';

import { t } from '@/i18n';
import { normalizeHoleEntry, normalizeHoleEvent, parseHandicapValue } from '@/features/hole/utils';
import type { HoleEntry, HoleEventEntry, Player, RoundSubgameEntry, Subgame, TeamSummary } from '@/features/hole/types';

type SessionUser = {
  id: string;
  token?: string | null;
};

type UseHoleDataParams = {
  apiBaseUrl: string;
  user: SessionUser | null;
  allowGuestClaimMode: boolean;
  claimPlayerId?: string | null;
  roundId: string | null;
  roundIdIsValid: boolean;
  isNavigationReady: boolean;
  setLoading: (value: boolean) => void;
  setError: (value: string | null) => void;
  setPlayers: (players: Player[]) => void;
  setTourId: (tourId: string) => void;
  setTeams: (teams: TeamSummary[]) => void;
  setLoggedInPlayerId: (value: string | null) => void;
  setAllHoleEntries: (entries: HoleEntry[]) => void;
  gameDefinitionIdsFromParamsCount: number;
  setSelectedGameDefinitionIds: (ids: string[]) => void;
  setHolesCount: (count: number) => void;
  setRoundSubgames: (entries: RoundSubgameEntry[]) => void;
  setHoleEvents: (events: HoleEventEntry[]) => void;
  setSubgames: (subgames: Subgame[]) => void;
  logWithContext: (event: string, payload: Record<string, unknown>, level?: 'debug' | 'warn' | 'error') => void;
};

type RawRoundGroupTeam = {
  teamId?: string;
  TeamId?: string;
  playerIds?: string[];
  PlayerIds?: string[];
};

type RawRoundGroup = {
  groupId?: string;
  GroupId?: string;
  teams?: RawRoundGroupTeam[];
  Teams?: RawRoundGroupTeam[];
};

type RoundGroupsResponse = {
  groups?: RawRoundGroup[];
  Groups?: RawRoundGroup[];
};

export type RoundPlayersSnapshot = {
  players: Player[];
  tourId?: string;
};

export type RoundMetadataSnapshot = {
  gameDefinitionIds: string[];
  holesCount: number;
};

const roundPlayersCache = new Map<string, RoundPlayersSnapshot>();
const roundPlayersRequests = new Map<string, Promise<RoundPlayersSnapshot>>();
const roundTeamsCache = new Map<string, TeamSummary[]>();
const roundTeamsRequests = new Map<string, Promise<TeamSummary[]>>();
const roundMetadataCache = new Map<string, RoundMetadataSnapshot>();
const roundMetadataRequests = new Map<string, Promise<RoundMetadataSnapshot>>();
const roundHoleEntriesCache = new Map<string, HoleEntry[]>();
const roundHoleEntriesRequests = new Map<string, Promise<HoleEntry[]>>();
const roundSubgamesCache = new Map<string, RoundSubgameEntry[]>();
const roundSubgamesRequests = new Map<string, Promise<RoundSubgameEntry[]>>();
const roundHoleEventsCache = new Map<string, HoleEventEntry[]>();
const roundHoleEventsRequests = new Map<string, Promise<HoleEventEntry[]>>();
const subgamesCache = new Map<string, Subgame[]>();
const subgamesRequests = new Map<string, Promise<Subgame[]>>();

const buildRoundCacheKey = (apiBaseUrl: string, roundId: string, userId: string | null | undefined) => {
  return `${apiBaseUrl}|${roundId}|${userId ?? 'anonymous'}`;
};

const buildUserCacheKey = (apiBaseUrl: string, userId: string | null | undefined) => {
  return `${apiBaseUrl}|${userId ?? 'anonymous'}`;
};

const buildRoundTeamsCacheKey = buildRoundCacheKey;

const normalizeStringList = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((item) => (typeof item === 'string' ? item.trim() : ''))
    .filter((item) => item.length > 0);
};

const normalizeHolesCount = (value: unknown): number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return parsed === 9 ? 9 : 18;
};

const clonePlayersSnapshot = (snapshot: RoundPlayersSnapshot): RoundPlayersSnapshot => ({
  players: snapshot.players.map((player) => ({ ...player })),
  tourId: snapshot.tourId,
});

const cloneMetadataSnapshot = (snapshot: RoundMetadataSnapshot): RoundMetadataSnapshot => ({
  gameDefinitionIds: [...snapshot.gameDefinitionIds],
  holesCount: snapshot.holesCount,
});

const normalizeRoundPlayers = (data: unknown): RoundPlayersSnapshot => {
  const record =
    data !== null && typeof data === 'object' && !Array.isArray(data)
      ? (data as { players?: unknown; Players?: unknown; tourId?: unknown; TourId?: unknown })
      : {};
  const rawList = Array.isArray(data)
    ? data
    : Array.isArray(record.players)
      ? record.players
      : Array.isArray(record.Players)
        ? record.Players
        : [];
  const players = rawList.map((player) => {
    const raw = player as Player & {
      handicap?: unknown;
      Handicap?: unknown;
      hcp?: unknown;
      Hcp?: unknown;
      playingHandicap?: unknown;
      playing_handicap?: unknown;
      imageKey?: string | null;
      ImageKey?: string | null;
      imageThumbKey?: string | null;
      ImageThumbKey?: string | null;
    };
    const handicap = parseHandicapValue(
      raw.handicap ?? raw.Handicap ?? raw.hcp ?? raw.Hcp ?? raw.playingHandicap ?? raw.playing_handicap
    );
    const imageKey = raw.imageKey ?? raw.ImageKey ?? null;
    const imageThumbKey = raw.imageThumbKey ?? raw.ImageThumbKey ?? null;
    return { ...raw, handicap, imageKey, imageThumbKey };
  });
  const tourId = typeof record.tourId === 'string' ? record.tourId : typeof record.TourId === 'string' ? record.TourId : undefined;
  return { players, tourId };
};

const normalizeRoundTeams = (data: RoundGroupsResponse): TeamSummary[] => {
  const groups = data.groups ?? data.Groups ?? [];
  return groups.flatMap((group, groupIndex) => {
    const teams = group.teams ?? group.Teams ?? [];
    return teams.map((team, teamIndex) => ({
      id: team.teamId ?? team.TeamId ?? `${group.groupId ?? group.GroupId ?? groupIndex}:${teamIndex}`,
      players: team.playerIds ?? team.PlayerIds ?? [],
    }));
  });
};

const normalizeRoundMetadata = (data: unknown): RoundMetadataSnapshot => {
  const record = data !== null && typeof data === 'object' ? (data as Record<string, unknown>) : {};
  return {
    gameDefinitionIds: normalizeStringList(record.gameDefinitionIds ?? record.GameDefinitionIds),
    holesCount: normalizeHolesCount(record.holesCount ?? record.HolesCount),
  };
};

const normalizeRoundSubgames = (data: unknown): RoundSubgameEntry[] => {
  const raw = Array.isArray(data) ? data : [];
  return raw
    .map((item) => {
      const record = item as Record<string, unknown>;
      const playerId = String(record.playerId ?? record.PlayerId ?? '');
      const subgameId = String(record.subgameId ?? record.SubgameId ?? '');
      const holeRaw = record.holeNumber ?? record.HoleNumber;
      const holeNumber = typeof holeRaw === 'number' ? holeRaw : Number(holeRaw);
      if (!playerId || !subgameId || !Number.isFinite(holeNumber)) {
        return null;
      }
      return { playerId, subgameId, holeNumber } satisfies RoundSubgameEntry;
    })
    .filter((entry): entry is RoundSubgameEntry => entry !== null);
};

const normalizeHoleEvents = (data: unknown): HoleEventEntry[] => {
  const raw = Array.isArray(data) ? data : [];
  return raw
    .map((item) => normalizeHoleEvent(item as Record<string, unknown>))
    .filter((entry): entry is HoleEventEntry => entry !== null);
};

const normalizeSubgameDefinitions = (data: unknown): Subgame[] => {
  const raw = Array.isArray(data) ? data : [];
  return raw
    .map((item) => {
      const record = item as Record<string, unknown>;
      const id = String(record.id ?? record.Id ?? '').trim();
      const name = String(record.name ?? record.Name ?? '').trim();
      if (!id || !name) {
        return null;
      }
      const descriptionRaw = record.description ?? record.Description;
      const subgame: Subgame = { id, name };
      if (typeof descriptionRaw === 'string') {
        subgame.description = descriptionRaw;
      }
      return subgame;
    })
    .filter((entry): entry is Subgame => entry !== null);
};

export const cacheRoundPlayersSnapshot = (
  apiBaseUrl: string,
  roundId: string | null | undefined,
  userId: string | null | undefined,
  snapshot: RoundPlayersSnapshot
) => {
  if (!roundId) return;
  roundPlayersCache.set(buildRoundCacheKey(apiBaseUrl, roundId, userId), clonePlayersSnapshot(snapshot));
};

export const cacheRoundMetadataSnapshot = (
  apiBaseUrl: string,
  roundId: string | null | undefined,
  userId: string | null | undefined,
  snapshot: Partial<RoundMetadataSnapshot>
) => {
  if (!roundId) return;
  const cacheKey = buildRoundCacheKey(apiBaseUrl, roundId, userId);
  const previous = roundMetadataCache.get(cacheKey);
  roundMetadataCache.set(cacheKey, {
    gameDefinitionIds: snapshot.gameDefinitionIds ? [...snapshot.gameDefinitionIds] : previous?.gameDefinitionIds ?? [],
    holesCount: normalizeHolesCount(snapshot.holesCount ?? previous?.holesCount ?? 18),
  });
};

export const cacheRoundHoleEntriesSnapshot = (
  apiBaseUrl: string,
  roundId: string | null | undefined,
  userId: string | null | undefined,
  entries: HoleEntry[]
) => {
  if (!roundId) return;
  roundHoleEntriesCache.set(buildRoundCacheKey(apiBaseUrl, roundId, userId), [...entries]);
};

export const cacheRoundHoleEventsSnapshot = (
  apiBaseUrl: string,
  roundId: string | null | undefined,
  userId: string | null | undefined,
  events: HoleEventEntry[]
) => {
  if (!roundId) return;
  roundHoleEventsCache.set(buildRoundCacheKey(apiBaseUrl, roundId, userId), [...events]);
};

export const useHoleData = ({
  apiBaseUrl,
  user,
  allowGuestClaimMode,
  claimPlayerId,
  roundId,
  roundIdIsValid,
  isNavigationReady,
  setLoading,
  setError,
  setPlayers,
  setTourId,
  setTeams,
  setLoggedInPlayerId,
  setAllHoleEntries,
  gameDefinitionIdsFromParamsCount,
  setSelectedGameDefinitionIds,
  setHolesCount,
  setRoundSubgames,
  setHoleEvents,
  setSubgames,
  logWithContext,
}: UseHoleDataParams) => {
  const userToken = user?.token ?? null;
  const userId = user?.id ?? null;
  const logWithContextRef = useRef(logWithContext);

  useEffect(() => {
    logWithContextRef.current = logWithContext;
  }, [logWithContext]);

  useEffect(() => {
    if (!isNavigationReady || !roundId) return;
    if (allowGuestClaimMode) {
      setLoading(false);
      setError(null);
      return;
    }
    if (!userToken) {
      setLoading(false);
      return;
    }

    const cacheKey = buildRoundCacheKey(apiBaseUrl, roundId, userId);
    const cachedSnapshot = roundPlayersCache.get(cacheKey);
    if (cachedSnapshot) {
      const snapshot = clonePlayersSnapshot(cachedSnapshot);
      setPlayers(snapshot.players);
      if (snapshot.tourId) {
        setTourId(snapshot.tourId);
      }
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const fetchPlayers = async () => {
      try {
        setLoading(true);
        setError(null);
        let request = roundPlayersRequests.get(cacheKey);
        if (!request) {
          request = fetch(`${apiBaseUrl}/api/rounds/${roundId}/players`, {
            headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
          }).then(async (response) => {
            if (!response.ok) {
              throw new Error();
            }
            return normalizeRoundPlayers(await response.json());
          });
          roundPlayersRequests.set(cacheKey, request);
        }

        const snapshot = await request;
        cacheRoundPlayersSnapshot(apiBaseUrl, roundId, userId, snapshot);
        if (!cancelled) {
          const next = clonePlayersSnapshot(snapshot);
          setPlayers(next.players);
          if (next.tourId) {
            setTourId(next.tourId);
          }
        }
      } catch {
        if (!cancelled && !allowGuestClaimMode) {
          setError(t('hole.playersLoadFailed'));
        }
      } finally {
        roundPlayersRequests.delete(cacheKey);
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchPlayers();
    return () => {
      cancelled = true;
    };
  }, [allowGuestClaimMode, apiBaseUrl, isNavigationReady, roundId, setError, setLoading, setPlayers, setTourId, userId, userToken]);

  useEffect(() => {
    if (!isNavigationReady || !roundIdIsValid || !roundId) return;
    if (allowGuestClaimMode) {
      return;
    }
    if (!userToken) {
      return;
    }

    const cacheKey = buildRoundTeamsCacheKey(apiBaseUrl, roundId, userId);
    const cachedTeams = roundTeamsCache.get(cacheKey);
    if (cachedTeams) {
      setTeams(cachedTeams);
      return;
    }

    const fetchTeams = async () => {
      try {
        let request = roundTeamsRequests.get(cacheKey);
        if (!request) {
          request = fetch(`${apiBaseUrl}/api/rounds/${roundId}/groups`, {
            headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
          }).then(async (response) => {
            if (!response.ok) {
              throw new Error(`Failed to load groups (${response.status})`);
            }

            const data = (await response.json()) as RoundGroupsResponse;
            return normalizeRoundTeams(data);
          });
          roundTeamsRequests.set(cacheKey, request);
        }

        const mappedTeams = await request;
        roundTeamsCache.set(cacheKey, mappedTeams);

        setTeams(mappedTeams);
        logWithContextRef.current('hole.teams.loaded', {
          message: 'Loaded group-derived teams for round.',
          teamCount: mappedTeams.length,
        });
      } catch (err) {
        logWithContextRef.current(
          'hole.teams.error',
          { message: err instanceof Error ? err.message : 'Failed to load group-derived teams.' },
          'warn'
        );
        setTeams([]);
      } finally {
        roundTeamsRequests.delete(cacheKey);
      }
    };

    void fetchTeams();
  }, [allowGuestClaimMode, apiBaseUrl, isNavigationReady, roundId, roundIdIsValid, setTeams, userId, userToken]);

  useEffect(() => {
    if (!isNavigationReady || !roundIdIsValid || !roundId) {
      if (gameDefinitionIdsFromParamsCount === 0) {
        setSelectedGameDefinitionIds([]);
      }
      setHolesCount(18);
      return;
    }
    if (allowGuestClaimMode) {
      return;
    }
    if (!userToken) {
      if (gameDefinitionIdsFromParamsCount === 0) {
        setSelectedGameDefinitionIds([]);
      }
      return;
    }

    const cacheKey = buildRoundCacheKey(apiBaseUrl, roundId, userId);
    const cachedSnapshot = roundMetadataCache.get(cacheKey);
    const applyMetadata = (snapshot: RoundMetadataSnapshot) => {
      if (snapshot.gameDefinitionIds.length > 0 || gameDefinitionIdsFromParamsCount === 0) {
        setSelectedGameDefinitionIds(snapshot.gameDefinitionIds);
      }
      setHolesCount(snapshot.holesCount);
    };
    if (cachedSnapshot) {
      applyMetadata(cloneMetadataSnapshot(cachedSnapshot));
      return;
    }

    let cancelled = false;
    const fetchRoundMetadata = async () => {
      try {
        let request = roundMetadataRequests.get(cacheKey);
        if (!request) {
          request = fetch(`${apiBaseUrl}/api/rounds/${roundId}`, {
            headers: { Authorization: `Bearer ${userToken}` },
          }).then(async (response) => {
            if (!response.ok) {
              throw new Error(`Failed to load round (${response.status})`);
            }
            return normalizeRoundMetadata(await response.json());
          });
          roundMetadataRequests.set(cacheKey, request);
        }
        const snapshot = await request;
        cacheRoundMetadataSnapshot(apiBaseUrl, roundId, userId, snapshot);
        if (!cancelled) {
          applyMetadata(snapshot);
        }
      } catch {
        if (!cancelled && gameDefinitionIdsFromParamsCount === 0) {
          setSelectedGameDefinitionIds([]);
          setHolesCount(18);
        }
      } finally {
        roundMetadataRequests.delete(cacheKey);
      }
    };

    void fetchRoundMetadata();
    return () => {
      cancelled = true;
    };
  }, [
    allowGuestClaimMode,
    apiBaseUrl,
    gameDefinitionIdsFromParamsCount,
    isNavigationReady,
    roundId,
    roundIdIsValid,
    setHolesCount,
    setSelectedGameDefinitionIds,
    userId,
    userToken,
  ]);

  useEffect(() => {
    if (allowGuestClaimMode && claimPlayerId) {
      setLoggedInPlayerId(claimPlayerId);
      return;
    }

    if (!userId || !userToken) {
      setLoggedInPlayerId(null);
      return;
    }

    let cancelled = false;
    const fetchCurrentPlayer = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/api/player/by-user/${userId}`, {
          headers: { Authorization: `Bearer ${userToken}` },
        });
        if (!response.ok) {
          if (!cancelled) {
            setLoggedInPlayerId(null);
          }
          return;
        }
        const payload = (await response.json()) as { id?: string };
        if (!cancelled) {
          setLoggedInPlayerId(payload.id ?? null);
        }
      } catch {
        if (!cancelled) {
          setLoggedInPlayerId(null);
        }
      }
    };

    void fetchCurrentPlayer();
    return () => {
      cancelled = true;
    };
  }, [allowGuestClaimMode, apiBaseUrl, claimPlayerId, setLoggedInPlayerId, userId, userToken]);

  useEffect(() => {
    if (!roundId || !isNavigationReady || !roundIdIsValid) {
      setRoundSubgames([]);
      return;
    }
    if (allowGuestClaimMode) {
      setRoundSubgames([]);
      return;
    }
    if (!userToken) {
      setRoundSubgames([]);
      return;
    }

    const cacheKey = buildRoundCacheKey(apiBaseUrl, roundId, userId);
    const cachedSubgames = roundSubgamesCache.get(cacheKey);
    if (cachedSubgames) {
      setRoundSubgames(cachedSubgames);
      return;
    }

    let cancelled = false;
    const fetchRoundSubgames = async () => {
      try {
        let request = roundSubgamesRequests.get(cacheKey);
        if (!request) {
          request = fetch(`${apiBaseUrl}/api/rounds/${roundId}/subgames`, {
            headers: { Authorization: `Bearer ${userToken}` },
          }).then(async (response) => {
            if (!response.ok) {
              throw new Error(`Failed to load round subgames (${response.status})`);
            }
            return normalizeRoundSubgames(await response.json());
          });
          roundSubgamesRequests.set(cacheKey, request);
        }

        const entries = await request;
        roundSubgamesCache.set(cacheKey, entries);
        if (!cancelled) {
          setRoundSubgames(entries);
        }
      } catch {
        if (!cancelled) {
          setRoundSubgames([]);
        }
      } finally {
        roundSubgamesRequests.delete(cacheKey);
      }
    };

    void fetchRoundSubgames();
    return () => {
      cancelled = true;
    };
  }, [allowGuestClaimMode, apiBaseUrl, isNavigationReady, roundId, roundIdIsValid, setRoundSubgames, userId, userToken]);

  useEffect(() => {
    if (!roundId || !isNavigationReady || !roundIdIsValid) {
      setHoleEvents([]);
      return;
    }
    if (allowGuestClaimMode) {
      setHoleEvents([]);
      return;
    }
    if (!userToken) {
      setHoleEvents([]);
      return;
    }

    const cacheKey = buildRoundCacheKey(apiBaseUrl, roundId, userId);
    const cachedEvents = roundHoleEventsCache.get(cacheKey);
    if (cachedEvents) {
      setHoleEvents(cachedEvents);
      return;
    }

    let cancelled = false;
    const fetchHoleEvents = async () => {
      try {
        let request = roundHoleEventsRequests.get(cacheKey);
        if (!request) {
          request = fetch(`${apiBaseUrl}/api/rounds/${roundId}/hole-events`, {
            headers: { Authorization: `Bearer ${userToken}` },
          }).then(async (response) => {
            if (!response.ok) {
              throw new Error(`Failed to load hole events (${response.status})`);
            }
            return normalizeHoleEvents(await response.json());
          });
          roundHoleEventsRequests.set(cacheKey, request);
        }

        const events = await request;
        cacheRoundHoleEventsSnapshot(apiBaseUrl, roundId, userId, events);
        if (!cancelled) {
          setHoleEvents(events);
        }
      } catch {
        if (!cancelled) {
          setHoleEvents([]);
        }
      } finally {
        roundHoleEventsRequests.delete(cacheKey);
      }
    };

    void fetchHoleEvents();
    return () => {
      cancelled = true;
    };
  }, [allowGuestClaimMode, apiBaseUrl, isNavigationReady, roundId, roundIdIsValid, setHoleEvents, userId, userToken]);

  useEffect(() => {
    if (!isNavigationReady) {
      setSubgames([]);
      return;
    }
    if (allowGuestClaimMode) {
      setSubgames([]);
      return;
    }
    if (!userToken) {
      setSubgames([]);
      return;
    }

    const cacheKey = buildUserCacheKey(apiBaseUrl, userId);
    const cachedSubgames = subgamesCache.get(cacheKey);
    if (cachedSubgames) {
      setSubgames(cachedSubgames);
      return;
    }

    let cancelled = false;
    const fetchSubgameDefinitions = async () => {
      try {
        let request = subgamesRequests.get(cacheKey);
        if (!request) {
          request = fetch(`${apiBaseUrl}/api/subgames`, {
            headers: { Authorization: `Bearer ${userToken}` },
          }).then(async (response) => {
            if (!response.ok) {
              throw new Error(`Failed to load subgames (${response.status})`);
            }
            return normalizeSubgameDefinitions(await response.json());
          });
          subgamesRequests.set(cacheKey, request);
        }

        const entries = await request;
        subgamesCache.set(cacheKey, entries);
        if (!cancelled) {
          setSubgames(entries);
        }
      } catch {
        if (!cancelled) {
          setSubgames([]);
        }
      } finally {
        subgamesRequests.delete(cacheKey);
      }
    };

    void fetchSubgameDefinitions();
    return () => {
      cancelled = true;
    };
  }, [allowGuestClaimMode, apiBaseUrl, isNavigationReady, setSubgames, userId, userToken]);

  useEffect(() => {
    if (!roundId || !isNavigationReady) {
      setAllHoleEntries([]);
      return;
    }
    if (allowGuestClaimMode) {
      setAllHoleEntries([]);
      return;
    }
    if (!userToken) {
      setAllHoleEntries([]);
      return;
    }

    const cacheKey = buildRoundCacheKey(apiBaseUrl, roundId, userId);
    const cachedEntries = roundHoleEntriesCache.get(cacheKey);
    if (cachedEntries) {
      setAllHoleEntries(cachedEntries);
      return;
    }

    let cancelled = false;
    const fetchAllHoleEntries = async () => {
      try {
        let request = roundHoleEntriesRequests.get(cacheKey);
        if (!request) {
          request = fetch(`${apiBaseUrl}/api/rounds/${roundId}/hole-entries`, {
            headers: userToken ? { Authorization: `Bearer ${userToken}` } : undefined,
          }).then(async (response) => {
            if (!response.ok) {
              throw new Error(`Failed to load hole entries (${response.status})`);
            }
            const payload = (await response.json()) as Array<Record<string, unknown>>;
            return payload
              .map((entry) => normalizeHoleEntry(entry))
              .filter((entry) => entry.holeNumber > 0);
          });
          roundHoleEntriesRequests.set(cacheKey, request);
        }

        const normalized = await request;
        cacheRoundHoleEntriesSnapshot(apiBaseUrl, roundId, userId, normalized);
        if (!cancelled) {
          setAllHoleEntries(normalized);
        }
      } catch {
        // Keep menu functional with websocket-only updates if this request fails.
      } finally {
        roundHoleEntriesRequests.delete(cacheKey);
      }
    };

    void fetchAllHoleEntries();
    return () => {
      cancelled = true;
    };
  }, [allowGuestClaimMode, apiBaseUrl, isNavigationReady, roundId, setAllHoleEntries, userId, userToken]);
};
